/**
 * Program-aware portal copy helpers (Path-to-GO S25).
 * Callers must supply the family of the effective selected binding. HCP, CHSP,
 * RAC and unresolved/multi-program contexts must never inherit SAH language.
 */

export type PortalProgramFamily = 'RAC' | 'SAH' | 'OTHER' | 'MIXED' | 'UNKNOWN';

const RAC_MARKERS = new Set([
  'RESIDENTIAL',
  'AN_ACC',
  'ANACC',
  'RAC',
  'RAC_RESPITE',
  'RESIDENTIAL_AGED_CARE',
  'PERMANENT_RESIDENTIAL',
  'RESPITE',
]);

const SAH_MARKERS = new Set([
  'SUPPORT_AT_HOME',
  'SAH',
  'SAH_ONGOING',
  'SAH_TRANSITIONED_HCP',
  'SAH_RESTORATIVE',
  'SAH_END_OF_LIFE',
]);

const OTHER_MARKERS = new Set([
  'HCP',
  'HCP_ONGOING',
  'CHSP',
  'CHSP_CURRENT',
  'HOME_CARE_PACKAGE',
  'NDIS',
  'DVA',
  'TAC',
  'PRIVATE',
  'BROKERAGE',
  'TRANSITION_CARE',
  'NATSIFACP',
  'MPSP',
  'FLEXIBLE_CARE',
]);

function normalizeProgramToken(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
}

export function classifyPortalProgramFamily(
  fundingPrograms: readonly unknown[] | null | undefined,
): PortalProgramFamily {
  const tokens = (fundingPrograms ?? [])
    .map((item) => (typeof item === 'string' ? normalizeProgramToken(item) : ''))
    .filter(Boolean);

  if (tokens.length === 0) return 'UNKNOWN';

  const hasRac = tokens.some((token) => RAC_MARKERS.has(token));
  const hasSah = tokens.some((token) => SAH_MARKERS.has(token));
  const hasOther = tokens.some((token) => OTHER_MARKERS.has(token));

  if ([hasRac, hasSah, hasOther].filter(Boolean).length > 1) return 'MIXED';
  if (hasRac) return 'RAC';
  if (hasSah) return 'SAH';
  if (hasOther) return 'OTHER';
  return 'UNKNOWN';
}

export function portalStatementsSubtitle(family: PortalProgramFamily): string {
  switch (family) {
    case 'RAC':
      return 'Your residential aged care billing and statement history.';
    case 'MIXED':
      return 'Your aged care billing and statement history.';
    case 'SAH':
      return 'Your Support at Home monthly statement history.';
    case 'OTHER':
      return 'Your care program billing and statement history.';
    default:
      return 'Your aged care statement history.';
  }
}

export function portalStatementsVisibilityBlurb(family: PortalProgramFamily): string {
  switch (family) {
    case 'RAC':
      return 'Statements are generated from your residential billing records held by your provider. Internal claim preparation and regulator submission evidence remain in the staff portal.';
    case 'MIXED':
      return 'Statements are generated from the finance records held by your provider for your care programs. Internal claim preparation and regulator submission evidence remain in the staff portal.';
    case 'SAH':
      return 'Statements are generated from the same controlled Support at Home finance projection used by your provider. Internal claim preparation, remittance handling, and regulator submission evidence remain in the staff portal.';
    case 'OTHER':
      return 'Statements are generated from the finance records held by your provider for the selected care program. Internal claim preparation and regulator submission evidence remain in the staff portal.';
    default:
      return 'Statements are generated from the finance records held by your provider. Internal claim preparation and regulator submission evidence remain in the staff portal.';
  }
}

/** Authoritative SAH exit-notice maximum under the Support at Home program (two weeks). */
export const SAH_EXIT_NOTICE_DAYS = 14;

function hasPositiveDays(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

export function portalExitNoticeCopy(input: {
  family: PortalProgramFamily;
  exitNoticeDays: number | null | undefined;
}): {
  intro: string;
  noticeStrong: string;
  noticeDetail: string;
  budgetStrong: string | null;
  budgetDetail: string | null;
} {
  const agreementDays = hasPositiveDays(input.exitNoticeDays) ? input.exitNoticeDays : null;

  if (input.family === 'RAC') {
    // RAC without a recorded value refers to the residential agreement — never invent 14 days.
    if (agreementDays == null) {
      return {
        intro:
          'Under the Aged Care Act 2024, you have rights about choosing your residential aged care provider. Key points:',
        noticeStrong: 'Notice period as set out in your residential agreement',
        noticeDetail:
          'check your residential agreement for the exact exit notice requirements that apply to you.',
        budgetStrong: null,
        budgetDetail: null,
      };
    }
    return {
      intro:
        'Under the Aged Care Act 2024, you have rights about choosing your residential aged care provider. Key points:',
      noticeStrong: `Notice period of ${agreementDays} days`,
      noticeDetail:
        'or as set out in your residential agreement — check your agreement for the exact exit notice requirements that apply to you.',
      budgetStrong: null,
      budgetDetail: null,
    };
  }

  if (input.family === 'MIXED') {
    return {
      intro:
        'Your rights and notice terms depend on the care program you are viewing. Select a care program to see the matching agreement terms.',
      noticeStrong: 'Program-specific notice period required',
      noticeDetail:
        'select a care program and check its executed agreement before relying on a notice period.',
      budgetStrong: null,
      budgetDetail: null,
    };
  }

  if (input.family === 'UNKNOWN' || input.family === 'OTHER') {
    if (agreementDays == null) {
      return {
        intro:
          'Under the Aged Care Act 2024, you have the right to change your care provider. Key points:',
        noticeStrong: 'Notice period as set out in your service agreement',
        noticeDetail: 'as set out in your service agreement.',
        budgetStrong: null,
        budgetDetail: null,
      };
    }
    return {
      intro:
        'Under the Aged Care Act 2024, you have the right to change your care provider. Key points:',
      noticeStrong: `Maximum notice period of ${agreementDays} days`,
      noticeDetail: 'as set out in your service agreement.',
      budgetStrong: null,
      budgetDetail: null,
    };
  }

  // SAH: authoritative program rule is two weeks (14 days).
  const sahDays = SAH_EXIT_NOTICE_DAYS;
  const inconsistent = agreementDays != null && agreementDays !== SAH_EXIT_NOTICE_DAYS;

  return {
    intro:
      'Under the Aged Care Act 2024 and the Support at Home program, you have the right to change your care provider at any time. Key points:',
    noticeStrong: `Maximum notice period of ${sahDays} days`,
    noticeDetail: inconsistent
      ? `your agreement records ${agreementDays} days; under SAH, the maximum notice period is two weeks (${SAH_EXIT_NOTICE_DAYS} days). Confirm the figure that applies with your provider.`
      : 'under SAH, the maximum notice period is two weeks.',
    budgetStrong: 'Your unspent budget transfers with you',
    budgetDetail: 'any unspent funds in your budget will be transferred to your new provider.',
  };
}
